import { useCallback, useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/library/accordion";
import SearchableSelector from "@/components/ui/others/SearchableSelector";
import { ScoreRangeSlider } from "@/components/common/inputs/ScoreRangeSlider";
import HighlightText from "../HighlightText";
import { useIsSectionActive } from "../../context/AppliedFiltersContext";
import {
  useFilterValues,
  useNicheFilterUpdate,
} from "../../context/FilterContext";
import useQualification from "@/module/education/api/useQualification";

/**
 * Heuristic for picking a sensible default scale per qualification level.
 *
 * The schema doesn't (yet) discriminate "academic level" vs "test score" or
 * carry a default value type, so we sniff the level name. As soon as a real
 * `QualificationLevel.defaultValueType` (or similar) ships, swap this for
 * that column — the rest of the component already adapts dynamically.
 *
 * Returns:
 *   - max:  the slider's upper bound (e.g. 100 for percent, 10 for CGPA)
 *   - unit: human label shown under the slider
 *   - step: slider granularity
 */
function inferScale(levelName: string): {
  max: number;
  min: number;
  unit: string;
  step: number;
} {
  const name = levelName.toLowerCase();

  if (
    name.includes("ielts") ||
    name.includes("toefl") ||
    name.includes("pte") ||
    name.includes("gre") ||
    name.includes("gmat") ||
    name.includes("sat") ||
    name.includes("test")
  ) {
    if (name.includes("ielts") || name.includes("pte")) {
      return { min: 0, max: 9, unit: "Band", step: 0.5 };
    }
    return { min: 0, max: 340, unit: "Score", step: 1 };
  }

  if (
    name.includes("ug") ||
    name.includes("undergrad") ||
    name.includes("bachelor") ||
    name.includes("pg") ||
    name.includes("postgrad") ||
    name.includes("master") ||
    name.includes("phd") ||
    name.includes("doctor")
  ) {
    return { min: 0, max: 10, unit: "CGPA", step: 0.1 };
  }

  return { min: 0, max: 100, unit: "Percentage", step: 1 };
}

const HIGHEST_PARAM = "qualLevelId";
const minParam = (id: number | string) => `qualScoreMin_${id}`;
const maxParam = (id: number | string) => `qualScoreMax_${id}`;

export default function AcademicBackground() {
  const sectionActive = useIsSectionActive("academic");
  const { nicheFilters } = useFilterValues();
  const updateNiche = useNicheFilterUpdate();

  const { levels, isLoading } = useQualification();

  const sortedLevels = useMemo(
    () =>
      Array.isArray(levels)
        ? [...levels].sort((a: any, b: any) => {
            // Try to honour a `level` / `order` column if present, fall back
            // to id (insertion order). Either way the section just needs a
            // stable ordering.
            const aw = a.level ?? a.order ?? a.id ?? 0;
            const bw = b.level ?? b.order ?? b.id ?? 0;
            return aw - bw;
          })
        : [],
    [levels]
  );

  const highestQualValue = useMemo(() => {
    const v = nicheFilters[HIGHEST_PARAM];
    return v != null && v !== "" ? String(v) : undefined;
  }, [nicheFilters]);

  const handleHighestChange = useCallback(
    (value: string) => {
      updateNiche(HIGHEST_PARAM, value);
    },
    [updateNiche]
  );

  return (
    <div className="flex flex-col overflow-hidden">
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue={sectionActive ? "academic-background" : undefined}
      >
        <AccordionItem value="academic-background" className="border-none">
          <AccordionTrigger className="text-b3-mid font-semibold bg-transparent bg-[var(--color-green-1)] px-4 py-3 rounded-none hover:no-underline">
            <HighlightText text="Academic Background" />
          </AccordionTrigger>
          <AccordionContent className="px-5 py-2 bg-white">
            <div className="space-y-[14px] px-2 pb-4">
              <div className="space-y-2">
                <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-1.5">
                  <HighlightText text="Highest Qualification" />
                </Label>
                <SearchableSelector
                  value={highestQualValue}
                  onChange={handleHighestChange}
                  disabled={isLoading || sortedLevels.length === 0}
                  placeholder={
                    isLoading
                      ? "Loading qualifications..."
                      : sortedLevels.length === 0
                        ? "No qualification levels configured"
                        : "Select qualification"
                  }
                  searchInputPlaceholder="Search qualifications..."
                  triggerClassName="h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal shadow-none hover:bg-[#fcfcfc]"
                  options={sortedLevels.map((level: any) => ({
                    value: String(level.id),
                    name: String(level.name),
                  }))}
                />
              </div>

              {sortedLevels.map((level: any) => {
                const scale = inferScale(level.name ?? "");
                const minKey = minParam(level.id);
                const maxKey = maxParam(level.id);
                const rawMin = nicheFilters[minKey];
                const rawMax = nicheFilters[maxKey];
                const minValue =
                  rawMin != null && rawMin !== "" ? Number(rawMin) : scale.min;
                const maxValue =
                  rawMax != null && rawMax !== "" ? Number(rawMax) : scale.max;
                return (
                  <div key={level.id} className="space-y-2">
                    <div className="flex flex-col gap-0.5">
                      <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-0">
                        <HighlightText text={`${level.name} score`} />
                      </Label>
                      <span className="text-[12px] leading-[1.35] tracking-[-0.24px] text-[#697571] font-normal">
                        {scale.unit}
                      </span>
                    </div>
                    <ScoreRangeSlider
                      min={scale.min}
                      max={scale.max}
                      minValue={minValue}
                      maxValue={maxValue}
                      onMinChange={(v) => updateNiche(minKey, v)}
                      onMaxChange={(v) => updateNiche(maxKey, v)}
                      unit={scale.unit}
                      step={scale.step}
                    />
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
